import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import { AsYouType } from 'libphonenumber-js'
import moment from 'moment-timezone'
import React, { ReactElement } from 'react'
import SingleDateInputField from 'src/components/SingleDateInputField/SingleDateInputField'
import { useTranslate } from 'src/localization/Trns'

import { KybAddressFieldset } from './KybAddressFieldset'
import { kybInvalidMessage } from './kybErrorMessages'
import { kybOwnerCardStyle } from './kybModal.style'
import { ServerOwnerMessages } from './kybServerErrors'
import {
  BeneficialOwnerErrors,
  BeneficialOwnerValues,
  digitsOnly,
} from './paymentsKyb.validation'

export interface KybOwnerCardProps {
  index: number
  owner: BeneficialOwnerValues
  /** The one permanent slot vs. an entry on the beneficial-owner list. */
  variant: 'control' | 'beneficial'
  onUpdate: (partial: Partial<BeneficialOwnerValues>) => void
  onRemove?: () => void
  errors?: BeneficialOwnerErrors
  serverMessages?: ServerOwnerMessages
}

/**
 * One person in the KYB modal's step 2.
 *
 * The two slots mean different things, so they are different cards rather than
 * one card with role checkboxes: the control person is mandatory, unremovable,
 * and asks the single branching question (do they also hold 25%+). Everyone on
 * the list after it is a beneficial owner by construction, so their ownership %
 * is unconditional and needs no role selection at all.
 */
export function KybOwnerCard({
  index,
  owner,
  variant,
  onUpdate,
  onRemove,
  errors,
  serverMessages,
}: KybOwnerCardProps): ReactElement {
  const translate = useTranslate()
  const fieldError = (
    leaf: Exclude<keyof BeneficialOwnerErrors, 'address'>,
  ): string | undefined =>
    kybInvalidMessage(translate, errors?.[leaf]) ?? serverMessages?.[leaf]
  const isControl = variant === 'control'
  // A listed owner is a beneficial owner by being on the list; the control
  // person opts in.
  const showsOwnership = isControl ? owner.isBeneficialOwner : true

  return (
    <div className="KybOwnerCard" css={kybOwnerCardStyle}>
      <div className="KybOwnerCard-header">
        <h3 className="KybOwnerCard-title">
          {isControl
            ? translate('Control person')
            : translate('Beneficial owner ${number}', {
                number: String(index),
              })}
        </h3>
        {onRemove && (
          <Button
            variant="no-style"
            onClick={onRemove}
            testId={`kyb-owner-${index}-remove`}
          >
            {translate('Remove')}
          </Button>
        )}
      </div>

      <div className="KybOwnerCard-grid">
        <TextInput
          label={translate('First name')}
          requirement="required"
          value={owner.firstName}
          onChange={(e): void => onUpdate({ firstName: e.target.value })}
          errorText={fieldError('firstName')}
          testId={`kyb-owner-${index}-first-name`}
        />
        <TextInput
          label={translate('Last name')}
          requirement="required"
          value={owner.lastName}
          onChange={(e): void => onUpdate({ lastName: e.target.value })}
          errorText={fieldError('lastName')}
          testId={`kyb-owner-${index}-last-name`}
        />
        <SingleDateInputField
          className="span2"
          id={`kyb-owner-${index}-dob`}
          required
          fieldLabel={
            <>
              {translate('Date of birth')}
              <span className="KybOwnerCard-required" aria-hidden="true">
                {' *'}
              </span>
            </>
          }
          allowPastAndCurrentDates
          dateMoment={
            owner.dateOfBirth
              ? moment(owner.dateOfBirth, 'YYYY-MM-DD')
              : undefined
          }
          onDateMomentChange={(m): void =>
            onUpdate({ dateOfBirth: m ? m.format('YYYY-MM-DD') : '' })
          }
          errors={
            fieldError('dateOfBirth')
              ? [fieldError('dateOfBirth') as string]
              : undefined
          }
        />
        <TextInput
          className="span2"
          label={translate('Email')}
          requirement="required"
          type="email"
          value={owner.email}
          onChange={(e): void => onUpdate({ email: e.target.value })}
          errorText={fieldError('email')}
          testId={`kyb-owner-${index}-email`}
        />
        <TextInput
          className="span2"
          label={translate('Phone')}
          requirement="required"
          placeholder={translate('e.g. (212) 555-0123')}
          value={owner.phoneNumber}
          onChange={(e): void =>
            onUpdate({
              phoneNumber: new AsYouType('US').input(e.target.value),
            })
          }
          errorText={fieldError('phoneNumber')}
          testId={`kyb-owner-${index}-phone`}
        />
        <TextInput
          className="span2"
          label={translate('Social Security Number (SSN)')}
          requirement="required"
          // Mask the digits (shoulder-surfing) and keep them out of browser
          // autofill/history. inputMode surfaces the numeric keypad. A dedicated
          // masked/reveal control is a follow-up (ext-ui TextInput has none yet).
          type="password"
          inputMode="numeric"
          // Chrome ignores autoComplete="off" on a password-typed field and
          // offers saved credentials; "new-password" plus the manager opt-outs
          // is what actually suppresses it.
          autoComplete="new-password"
          name={`kyb-owner-${index}-ssn`}
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          value={owner.ssn}
          maxLength={9}
          onChange={(e): void =>
            onUpdate({ ssn: digitsOnly(e.target.value).slice(0, 9) })
          }
          errorText={fieldError('ssn')}
          testId={`kyb-owner-${index}-ssn`}
        />
        {isControl && (
          // The one branching question on this card: a control person may or
          // may not also hold a stake. Everyone on the list below is a
          // beneficial owner by construction and needs no such toggle.
          <label className="KybOwnerCard-roleOption span2">
            <Checkbox
              aria-label={translate(
                'This person also owns 25% or more of the business',
              )}
              checked={owner.isBeneficialOwner}
              onChange={(e): void =>
                onUpdate({
                  isBeneficialOwner: e.target.checked,
                  // Written on both toggles: unchecking keeps a stale
                  // percentage out of the request, and checking marks the
                  // just-revealed field as freshly edited so it is not
                  // pre-judged from a failed advance before this card grew it.
                  ownershipPercentage: '',
                })
              }
              data-testid={`kyb-owner-${index}-also-owner`}
            />
            <span>
              {translate('This person also owns 25% or more of the business')}
            </span>
          </label>
        )}
        {showsOwnership && (
          <TextInput
            className="span2"
            label={translate('Ownership % (25–100)')}
            requirement="required"
            value={owner.ownershipPercentage}
            onChange={(e): void =>
              onUpdate({ ownershipPercentage: e.target.value })
            }
            errorText={fieldError('ownershipPercentage')}
            testId={`kyb-owner-${index}-ownership`}
          />
        )}
      </div>

      <KybAddressFieldset
        legend={translate('Home address')}
        idPrefix={`kyb-owner-${index}-address`}
        value={owner.address}
        onChange={(address): void => onUpdate({ address })}
        errors={errors?.address}
        serverMessages={serverMessages?.address}
      />
    </div>
  )
}
